import request, { longRunningRequest } from '../../../../core/wrappers/axios-wrapper';
import { AIGeneratorCommandURL } from '../../../../types/constants/apiUrls/studio/AIGenerator';
import { RequestType } from '../../../../types/enums/request-enums';

interface GeneratePhotoRequest {
    prompt: string;
    imageUrls: string[];
    numImages: number;
    aspectRatio: string;
    outputFormat: string;
    resolution: string;
    quality?: string;
    username?: string;
    productBarcode?: string;
    fileName?: string;
    modelType?: number; // 0: EachLabs, 1: Google Gemini, 2: Azure OpenAI, 3: Google Gemini Flash, 4: AIGate, 5: Gemini Pro LCW, 6: AIGate Pro LCW, 7: Nano Banana 2.1, 8: Nano Banana 2.1 LCW
    base64Image?: string; // For Gemini image editing
    mimeType?: string; // For Gemini image editing
    maskBase64Image?: string; // For inpainting - mask image (white = edit, black = keep)
    maskMimeType?: string; // For inpainting - mask mime type
    isInpainting?: boolean; // Whether this is an inpainting request
    inpaintingSourceImageUrl?: string; // Original generated image URL for inpainting
    referenceBase64Image?: string; // Reference image base64 for inpainting consistency
    referenceMimeType?: string; // Reference image mime type
    generationTypeCode?: string; // AI generation type code for dynamic system prompt
}

// Backend response from GenerateMannequinPhotoResponse
interface GeneratePhotoResponse {
    predictionId: string;
    status: string;
    outputUrls: string[];
    errorMessage?: string;
    errorCode?: string;
    success: boolean;
}



// Backend response from GetAIGenerationStatusResponse
interface GetStatusResponse {
    predictionId: string;
    status: string;
    outputUrls: string[];
    errorMessage?: string;
    errorCode?: string;
    isComplete: boolean;
    success: boolean;
}

const GeneratePhoto = async (data: GeneratePhotoRequest): Promise<GeneratePhotoResponse> => {
    try {
        // Uzun süren fotoğraf üretme işlemi için özel request fonksiyonu kullan
        const response = await longRunningRequest({
            url: AIGeneratorCommandURL.Generate,
            method: RequestType.POST,
            data,
            skipErrorModal: true,
        });
        return response;
    } catch (error) {
        console.error('❌ GeneratePhoto API Error:', error);
        throw error;
    }
};

const GetGenerationStatus = async (predictionId: string): Promise<GetStatusResponse> => {
    try {
        const response = await request({
            url: AIGeneratorCommandURL.GetStatus(predictionId),
            method: RequestType.GET,
            skipErrorModal: true,
        });
        return response;
    } catch (error) {
        console.error('❌ GetGenerationStatus API Error:', error);
        throw error;
    }
};


const DownloadImage = async (url: string, filename: string, predictionId?: string): Promise<void> => {
    const response = await request({
        url: AIGeneratorCommandURL.Download(url, filename, predictionId),
        method: RequestType.GET,
        responseType: 'blob',
        skipBlobAutoOpen: true,
    });

    const blob = response as Blob;
    const blobUrl = globalThis.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    globalThis.URL.revokeObjectURL(blobUrl);
};

/**
 * Fetches an image URL through the BFF proxy and returns it as a base64 data URL.
 * Useful for S3 URLs that have CORS restrictions or require authentication.
 * @param imageUrl - The image URL to fetch
 * @returns Promise<string> - Base64 encoded data URL (e.g., "data:image/jpeg;base64,...")
 */
const FetchImageAsBase64 = async (imageUrl: string): Promise<string> => {
    const response = await request({
        url: AIGeneratorCommandURL.Download(imageUrl, 'image'),
        method: RequestType.GET,
        responseType: 'arraybuffer',
    });

    const uint8Array = new Uint8Array(response);
    let binary = '';
    for (let i = 0; i < uint8Array.length; i++) {
        binary += String.fromCharCode(uint8Array[i]);
    }
    const base64 = btoa(binary);

    // Detect MIME type from URL extension, default to jpeg
    const extMatch = imageUrl.match(/\.(jpg|jpeg|png|webp|gif)(?:\?|$)/i);
    const ext = extMatch ? extMatch[1].toLowerCase() : 'jpeg';
    const mimeType = ext === 'jpg' ? 'image/jpeg' : `image/${ext}`;

    return `data:${mimeType};base64,${base64}`;
};

interface EnhancePromptResponse {
    originalPrompt: string;
    enhancedPrompt: string;
    summary?: string;
    success: boolean;
    errorMessage?: string;
    errorCode?: string;
}

const EnhancePrompt = async (prompt: string): Promise<EnhancePromptResponse> => {
    const response = await request({
        url: AIGeneratorCommandURL.EnhancePrompt,
        method: RequestType.POST,
        data: { prompt },
        skipErrorModal: true,
    });
    return response;
};

const MarkAsDownloaded = async (predictionId: string): Promise<void> => {
    try {
        await request({
            url: AIGeneratorCommandURL.MarkAsDownloaded(predictionId),
            method: RequestType.PUT,
        });
    } catch (error) {
        console.error('❌ MarkAsDownloaded API Error:', error);
    }
};

const AIGeneratorCommandService = {
    GeneratePhoto,
    GetGenerationStatus,
    DownloadImage,
    FetchImageAsBase64,
    EnhancePrompt,
    MarkAsDownloaded,
};

export default AIGeneratorCommandService;

